import React, { useEffect, useState } from 'react';
import Sidebar from './components/Sidebar';
import OverviewPage from './pages/OverviewPage';
import PromptsPage from './pages/PromptsPage';
import DomainsPage from './pages/DomainsPage';
import GapAnalysisPage from './pages/GapAnalysisPage';
import EarnedPage from './pages/EarnedPage';
import OwnedPage from './pages/OwnedPage';
import GeoAgentPage from './pages/GeoAgentPage';
import MaterialsPage from './pages/MaterialsPage';
import InsightsPage from './pages/InsightsPage';
import ImpactPage from './pages/ImpactPage';
import LandingPage from './pages/LandingPage';

import ProfilePage from './pages/ProfilePage';
import BrandsPage from './pages/BrandsPage';
import SettingsPage from './pages/SettingsPage';
import ProjectsPage from './pages/ProjectsPage';
import ApiKeysPage from './pages/ApiKeysPage';
import MembersPage from './pages/MembersPage';
import BillingPage from './pages/BillingPage';
import { I18nProvider } from './i18n/I18nContext';
import { peecAPI } from './api/peecAPI';
import type { WorkspaceItem } from './db/indexedDB';
import './App.css';

function App() {
  const [activeMenu, setActiveMenu] = useState('overview');
  const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([]);
  const [activeWorkspace, setActiveWorkspace] = useState<WorkspaceItem | null>(null);

  useEffect(() => {
    peecAPI.listWorkspaces()
      .then((items) => {
        const sorted = [...items].sort((a, b) =>
          a.id === 'workspace_tradingbase' ? -1 : b.id === 'workspace_tradingbase' ? 1 : 0
        );
        setWorkspaces(sorted);
        setActiveWorkspace((current) => current ?? sorted[0] ?? null);
      })
      .catch((error) => {
        console.error('Failed to fetch workspaces:', error);
      });
  }, []);

  useEffect(() => {
    if (!activeWorkspace && workspaces.length > 0) {
      setActiveWorkspace(workspaces[0]);
    }
  }, [activeWorkspace, workspaces]);

  if (activeMenu === 'landing') {
    return (
      <I18nProvider>
        <LandingPage onEnterApp={() => setActiveMenu('overview')} />
      </I18nProvider>
    );
  }

  if (!activeWorkspace) {
    return (
      <I18nProvider>
        <div className="flex h-screen items-center justify-center bg-zinc-50 text-sm text-zinc-500">
          Loading...
        </div>
      </I18nProvider>
    );
  }

  const companyId = activeWorkspace.id;

  const renderPage = () => {
    switch (activeMenu) {
      case 'overview':
        return <OverviewPage workspaceId={companyId} />;
      case 'prompts':
        return <PromptsPage workspaceId={companyId} />;
      case 'domains':
        return <DomainsPage key="domains" workspaceId={companyId} />;
      case 'gap-analysis':
        return <GapAnalysisPage workspaceId={companyId} />;
      case 'earned':
        return <EarnedPage workspaceId={companyId} onQuickNavigate={setActiveMenu} />;
      case 'owned':
        return <OwnedPage workspaceId={companyId} />;
      case 'materials':
        return <MaterialsPage workspaceId={companyId} />;
      case 'insights':
        return <InsightsPage workspaceId={companyId} />;
      case 'geo-agent':
        return <GeoAgentPage workspaceId={companyId} />;

      case 'impact':
        return <ImpactPage workspaceId={companyId} />;
      case 'profile':
        return <ProfilePage workspaceId={companyId} />;
      case 'brands':
        return <BrandsPage workspaceId={companyId} />;
      case 'settings':
        return <SettingsPage workspaceId={companyId} />;
      case 'projects':
        return <ProjectsPage workspaceId={companyId} />;
      case 'api-keys':
        return <ApiKeysPage workspaceId={companyId} />;
      case 'members':
        return <MembersPage workspaceId={companyId} />;
      case 'billing':
        return <BillingPage workspaceId={companyId} />;
      default:
        return <OverviewPage workspaceId={companyId} />;
    }
  };

  return (
    <I18nProvider>
      <div className="flex h-screen bg-gray-50">
        <Sidebar
          activeMenu={activeMenu}
          onMenuChange={setActiveMenu}
          workspaces={workspaces}
          activeWorkspace={activeWorkspace}
          onWorkspaceChange={setActiveWorkspace}
        />
        <div className="flex-1 overflow-auto lg:ml-0">
          {renderPage()}
        </div>
      </div>
    </I18nProvider>
  );
}

export default App;
